import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useContracts, useClients, useConvenios } from '../../contexts/DataContext';
import { useUser } from '../../components/UserContext';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../../components/table';
import { FianzaView } from '../../components/FianzaView';
import TablePager from '../../components/TablePager';
import { facturasPorPagar, DIAS_BURO, DIAS_AVISO } from '../../utils/saldosCliente';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);

// Aging buckets over `dias` (days since the factura was issued). The 90+ bucket
// is exactly the buró threshold from saldosCliente.
const BUCKETS = [
  { key: 'b0', label: '0-30', test: (d) => d !== null && d <= 30 },
  { key: 'b31', label: '31-60', test: (d) => d !== null && d > 30 && d < DIAS_AVISO },
  { key: 'b61', label: `${DIAS_AVISO}-${DIAS_BURO - 1}`, test: (d) => d !== null && d >= DIAS_AVISO && d < DIAS_BURO },
  { key: 'b90', label: `${DIAS_BURO}+`, test: (d) => d !== null && d >= DIAS_BURO },
  { key: 'sinFecha', label: 'Sin fecha', test: (d) => d === null },
];

const bucketOf = (dias) => BUCKETS.find((b) => b.test(dias))?.key || 'sinFecha';

// Convenios and clientes share the Cliente column, so the convenio rows carry
// the same cian-digital mark they get in Pulso and en la vista de contrato.
const ConvenioChip = () => (
  <span className="ml-1.5 align-middle inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-[#53B5CC]/15 text-[#53B5CC] border border-[#53B5CC]/30">
    Convenio
  </span>
);

const diasBadge = (dias) => {
  if (dias === null) return 'bg-gray-100 text-gray-600';
  if (dias >= DIAS_BURO) return 'bg-red-100 text-red-800';
  if (dias >= DIAS_AVISO) return 'bg-amber-100 text-amber-800';
  return 'bg-gray-100 text-gray-700';
};

export function CobranzaPrimas() {
  const { contractsList } = useContracts();
  const { clientsById } = useClients();
  const { conveniosById } = useConvenios();
  const { user } = useUser();
  const uid = user?.uid || null;

  const location = useLocation();
  const navigate = useNavigate();

  const [groupBy, setGroupBy] = useState('cliente');
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const [selectedFianza, setSelectedFianza] = useState(null);
  const [isFianzaOpen, setIsFianzaOpen] = useState(false);
  const [soloMios, setSoloMios] = useState(false);
  const perPage = 10;
  const perPageRows = 12;

  // ?filtro=riesgo lives in the URL so the Home tile ("N en riesgo de buró")
  // can deep-link straight to those facturas as a flat list — the grouped view
  // only shows them as bucket totals.
  const soloRiesgo = new URLSearchParams(location.search).get('filtro') === 'riesgo';

  const setFiltro = (next) => {
    const params = new URLSearchParams(location.search);
    if (next === 'riesgo') params.set('filtro', 'riesgo');
    else params.delete('filtro');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    setExpandedKeys(new Set());
    setPage(1);
  };

  const contractsById = useMemo(() => {
    const map = new Map();
    contractsList.forEach((c) => map.set(c.id, c));
    return map;
  }, [contractsList]);

  const rows = useMemo(() => {
    return facturasPorPagar(contractsList, Date.now()).map((row) => ({
      ...row,
      clienteNombre: row.clientId
        ? clientsById[row.clientId]?.nombreEmpresa || 'Sin nombre'
        : row.convenioId
        ? conveniosById?.[row.convenioId]?.nombre || 'Convenio sin nombre'
        : 'Sin cliente',
      esConvenio: Boolean(row.convenioId),
      // Responsable = assignedUsers del cliente ∪ los del contrato, igual que
      // en Pulso y en Producción por responsable.
      esMio: uid
        ? [
            ...(clientsById[row.clientId]?.assignedUsers || []),
            ...(contractsById.get(row.contratoId)?.assignedUsers || []),
          ].includes(uid)
        : false,
      bucket: bucketOf(row.dias),
    }));
  }, [contractsList, clientsById, conveniosById, contractsById, uid]);

  const misCount = useMemo(() => rows.filter((r) => r.esMio).length, [rows]);

  // Everything downstream — tiles, grupos y la lista de riesgo — cuelga de aquí,
  // so the totals never contradict the table below them.
  const visibleRows = useMemo(
    () => (soloMios ? rows.filter((r) => r.esMio) : rows),
    [rows, soloMios]
  );

  const totals = useMemo(() => {
    const t = { total: 0, count: visibleRows.length, riesgo: 0, riesgoCount: 0, aviso: 0 };
    visibleRows.forEach((r) => {
      t.total += r.prima;
      if (r.bucket === 'b90') {
        t.riesgo += r.prima;
        t.riesgoCount += 1;
      } else if (r.bucket === 'b61') {
        t.aviso += r.prima;
      }
    });
    return t;
  }, [visibleRows]);

  const groups = useMemo(() => {
    const map = new Map();
    visibleRows.forEach((r) => {
      // Key on the id, not the name: two clientes (or a cliente and a convenio)
      // can share a name and would otherwise collapse into one row.
      const key =
        groupBy === 'cliente'
          ? r.convenioId
            ? `conv:${r.convenioId}`
            : r.clientId
            ? `cli:${r.clientId}`
            : 'sin-cliente'
          : `afi:${r.afianzadora}`;
      const entry = map.get(key) || {
        key,
        label: groupBy === 'cliente' ? r.clienteNombre : r.afianzadora,
        esConvenio: groupBy === 'cliente' && r.esConvenio,
        total: 0,
        count: 0,
        b0: 0,
        b31: 0,
        b61: 0,
        b90: 0,
        sinFecha: 0,
        rows: [],
      };
      entry.total += r.prima;
      entry.count += 1;
      entry[r.bucket] += r.prima;
      entry.rows.push(r);
      map.set(key, entry);
    });
    return Array.from(map.values()).sort((a, b) => (b.b90 - a.b90) || (b.total - a.total));
  }, [visibleRows, groupBy]);

  const pagedGroups = groups.slice((page - 1) * perPage, page * perPage);

  // facturasPorPagar already returns oldest-debt-first, and rows keeps that order.
  const riesgoRows = useMemo(() => visibleRows.filter((r) => r.bucket === 'b90'), [visibleRows]);
  const pagedRiesgo = riesgoRows.slice((page - 1) * perPageRows, page * perPageRows);

  const toggleExpanded = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const openFianza = (row) => {
    const contract = contractsList.find((c) => c.id === row.contratoId);
    const fianza = contract?.fianzas?.find((f) => f.id === row.fianzaId);
    if (!fianza) return;
    setSelectedFianza({ ...fianza, contratoId: row.contratoId, clientId: row.clientId });
    setIsFianzaOpen(true);
  };

  const StatTile = ({ label, value, sub, tone, onClick, active }) => {
    const cls = `rounded-lg border p-4 text-left ${tone || 'bg-white border-azul-surelia/20'} ${
      active ? 'ring-2 ring-azul-surelia/40' : ''
    } ${onClick ? 'cursor-pointer transition-shadow hover:shadow-sm' : ''}`;
    const body = (
      <>
        <Text className="text-xs text-azul-surelia/70 mb-1">{label}</Text>
        <Text className="text-xl font-bold text-azul-surelia tabular-nums">{value}</Text>
        {sub && <Text className="text-xs text-zinc-500 mt-0.5">{sub}</Text>}
      </>
    );
    return onClick ? (
      <button type="button" onClick={onClick} className={cls}>{body}</button>
    ) : (
      <div className={cls}>{body}</div>
    );
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Heading level={2} className="text-xl text-azul-surelia">Cobranza de primas</Heading>
          <Text className="text-sm text-azul-surelia/70">
            {soloRiesgo
              ? `Facturas con ${DIAS_BURO}+ días sin pago, de la más antigua a la más reciente`
              : 'Facturas sin pagar al día de hoy, por antigüedad'}
            {soloMios && ' · solo tus asignados'}
          </Text>
        </div>
        {/* Nothing in the app scopes data per usuario, so this is a personal
            lens over the same org-wide rows, not an access boundary. */}
        {misCount > 0 && (
          <button
            type="button"
            onClick={() => {
              setSoloMios((v) => !v);
              setExpandedKeys(new Set());
              setPage(1);
            }}
            className={`shrink-0 whitespace-nowrap px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
              soloMios
                ? 'bg-azul-surelia text-white border-azul-surelia'
                : 'bg-white text-azul-surelia border-azul-surelia/30 hover:bg-azul-surelia/5'
            }`}
          >
            {soloMios ? 'Ver toda la cartera' : `Ver mis asignados (${misCount})`}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <StatTile label="Total por cobrar" value={fmtMXN(totals.total)} sub={`${totals.count} facturas`} />
        <StatTile
          label={`En riesgo de buró (${DIAS_BURO}+ días)`}
          value={fmtMXN(totals.riesgo)}
          sub={totals.riesgoCount > 0 ? `${totals.riesgoCount} facturas` : null}
          tone={totals.riesgo > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-azul-surelia/20'}
          onClick={totals.riesgoCount > 0 ? () => setFiltro(soloRiesgo ? null : 'riesgo') : undefined}
          active={soloRiesgo}
        />
        <StatTile
          label={`Por llegar a riesgo (${DIAS_AVISO}-${DIAS_BURO - 1} días)`}
          value={fmtMXN(totals.aviso)}
          tone={totals.aviso > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-azul-surelia/20'}
        />
      </div>
      {/* Filters on their own row: inside the fourth tile the pills wrapped
          word by word as soon as the window got narrow. */}
      <div className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-2" data-pdf-hide>
        <div className="flex items-center gap-2">
          <Text className="text-xs text-azul-surelia/70 whitespace-nowrap">Vista</Text>
          <div className="flex gap-1">
            {[
              { key: null, label: 'Todas' },
              { key: 'riesgo', label: `En riesgo (${totals.riesgoCount})` },
            ].map((opt) => (
              <button
                key={opt.key || 'todas'}
                onClick={() => setFiltro(opt.key)}
                className={`whitespace-nowrap px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                  (opt.key === 'riesgo') === soloRiesgo
                    ? 'bg-azul-surelia text-white border-azul-surelia'
                    : 'bg-white text-azul-surelia border-azul-surelia/30 hover:bg-azul-surelia/5'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        {!soloRiesgo && (
          <div className="flex items-center gap-2">
            <Text className="text-xs text-azul-surelia/70 whitespace-nowrap">Agrupar por</Text>
            <div className="flex gap-1">
              {[
                { key: 'cliente', label: 'Cliente' },
                { key: 'afianzadora', label: 'Afianzadora' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => {
                    setGroupBy(opt.key);
                    setExpandedKeys(new Set());
                    setPage(1);
                  }}
                  className={`whitespace-nowrap px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                    groupBy === opt.key
                      ? 'bg-azul-surelia text-white border-azul-surelia'
                      : 'bg-white text-azul-surelia border-azul-surelia/30 hover:bg-azul-surelia/5'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {soloRiesgo ? (
        riesgoRows.length === 0 ? (
          <Text className="text-sm text-zinc-500 py-8 text-center">
            {soloMios ? 'Sin facturas en riesgo de buró entre tus asignados' : 'Sin facturas en riesgo de buró'}
          </Text>
        ) : (
          <>
            <div className="overflow-x-auto border border-azul-surelia/15 rounded-lg">
              <Table>
                <TableHead className="bg-azul-surelia text-white">
                  <TableRow>
                    <TableHeader className="text-white text-right">Días sin pago</TableHeader>
                    <TableHeader className="text-white">Fianza</TableHeader>
                    <TableHeader className="text-white">Cliente / Convenio</TableHeader>
                    <TableHeader className="text-white">Afianzadora</TableHeader>
                    <TableHeader className="text-white text-right">Emisión</TableHeader>
                    <TableHeader className="text-white text-right">Prima</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRiesgo.map((r) => (
                    <TableRow
                      key={`${r.fianzaId}-${r.facturaId || 'f'}`}
                      className="cursor-pointer hover:bg-azul-surelia/5"
                      onClick={() => openFianza(r)}
                    >
                      <TableCell className="py-3 text-right">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${diasBadge(r.dias)}`}>
                          {r.dias} días
                        </span>
                      </TableCell>
                      <TableCell className="py-3">
                        <Text className="text-sm font-mono text-zinc-700">{r.numeroFianza}</Text>
                      </TableCell>
                      <TableCell className="py-3">
                        <Text className="text-sm font-medium text-azul-surelia">
                          {r.clienteNombre}
                          {r.esConvenio && <ConvenioChip />}
                        </Text>
                      </TableCell>
                      <TableCell className="py-3">
                        <Text className="text-sm text-zinc-600" title={r.afianzadora}>{r.afianzadora}</Text>
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        <Text className="text-sm text-zinc-500">
                          {r.fechaMs ? new Date(r.fechaMs).toLocaleDateString('es-MX') : '—'}
                        </Text>
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        <Text className="text-sm font-semibold text-red-700 tabular-nums">{fmtMXN(r.prima)}</Text>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <TablePager
              total={riesgoRows.length}
              page={page}
              onPage={setPage}
              perPage={perPageRows}
              itemLabel="facturas"
            />
          </>
        )
      ) : groups.length === 0 ? (
        <Text className="text-sm text-zinc-500 py-8 text-center">
          {soloMios ? 'Sin facturas pendientes de pago entre tus asignados' : 'Sin facturas pendientes de pago'}
        </Text>
      ) : (
        <>
          <div className="overflow-x-auto border border-azul-surelia/15 rounded-lg">
            <Table>
              <TableHead className="bg-azul-surelia text-white">
                <TableRow>
                  <TableHeader className="text-white w-8"></TableHeader>
                  <TableHeader className="text-white">{groupBy === 'cliente' ? 'Cliente / Convenio' : 'Afianzadora'}</TableHeader>
                  {BUCKETS.map((b) => (
                    <TableHeader key={b.key} className="text-white text-right">{b.label}</TableHeader>
                  ))}
                  <TableHeader className="text-white text-right">Total</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedGroups.map((g) => {
                  const expanded = expandedKeys.has(g.key);
                  return (
                    <React.Fragment key={g.key}>
                      <TableRow
                        className="cursor-pointer hover:bg-azul-surelia/5"
                        onClick={() => toggleExpanded(g.key)}
                      >
                        <TableCell className="py-3 pr-0">
                          {expanded ? (
                            <ChevronDownIcon className="w-4 h-4 text-zinc-400" />
                          ) : (
                            <ChevronRightIcon className="w-4 h-4 text-zinc-400" />
                          )}
                        </TableCell>
                        <TableCell className="py-3">
                          <Text className="text-sm font-medium text-azul-surelia">
                            {g.label}
                            {g.esConvenio && <ConvenioChip />}
                          </Text>
                          <Text className="text-xs text-zinc-500">{g.count} {g.count === 1 ? 'factura' : 'facturas'}</Text>
                        </TableCell>
                        {BUCKETS.map((b) => (
                          <TableCell key={b.key} className="py-3 text-right">
                            <Text className={`text-sm tabular-nums ${
                              g[b.key] > 0
                                ? b.key === 'b90'
                                  ? 'text-red-600 font-semibold'
                                  : b.key === 'b61'
                                  ? 'text-amber-600 font-semibold'
                                  : 'text-zinc-700'
                                : 'text-zinc-300'
                            }`}>
                              {g[b.key] > 0 ? fmtMXN(g[b.key]) : '—'}
                            </Text>
                          </TableCell>
                        ))}
                        <TableCell className="py-3 text-right">
                          <Text className="text-sm font-bold text-azul-surelia tabular-nums">{fmtMXN(g.total)}</Text>
                        </TableCell>
                      </TableRow>
                      {expanded && (
                        <TableRow className="bg-azul-surelia/5">
                          <TableCell colSpan={8} className="py-2 px-6">
                            <div className="space-y-1">
                              {g.rows.map((r) => (
                                <button
                                  key={`${r.fianzaId}-${r.facturaId || 'f'}`}
                                  onClick={() => openFianza(r)}
                                  className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 text-left bg-white rounded-lg border border-zinc-200 px-3 py-1.5 hover:border-azul-surelia/40 transition-colors"
                                >
                                  <span className="text-sm font-mono text-zinc-700 min-w-[130px]">{r.numeroFianza}</span>
                                  <span className="text-xs text-zinc-500 min-w-[120px] truncate">
                                    {groupBy === 'cliente' ? r.afianzadora : r.clienteNombre}
                                    {groupBy !== 'cliente' && r.esConvenio && <ConvenioChip />}
                                  </span>
                                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${diasBadge(r.dias)}`}>
                                    {r.dias === null ? 'Sin fecha' : `${r.dias} días`}
                                  </span>
                                  {r.fechaMs && (
                                    <span className="text-xs text-zinc-500">
                                      {new Date(r.fechaMs).toLocaleDateString('es-MX')}
                                    </span>
                                  )}
                                  <span className="text-sm font-semibold text-azul-surelia tabular-nums ml-auto">
                                    {fmtMXN(r.prima)}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <TablePager
            total={groups.length}
            page={page}
            onPage={setPage}
            perPage={perPage}
            itemLabel={groupBy === 'cliente' ? 'clientes y convenios' : 'afianzadoras'}
          />
        </>
      )}

      {selectedFianza &&
        createPortal(
          <FianzaView
            isOpen={isFianzaOpen}
            onClose={() => {
              setIsFianzaOpen(false);
              setSelectedFianza(null);
            }}
            fianza={selectedFianza}
            clientData={clientsById[selectedFianza.clientId]}
            contratoData={contractsList.find((c) => c.id === selectedFianza.contratoId)}
            onUpdate={() => {}}
          />,
          document.body
        )}
    </div>
  );
}
